import { useState, useEffect } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ArrowUpRight, Menu } from 'lucide-react';
import SyenovixLogo from './SyenovixLogo';

const ACCENT = '#5E0ED7';
const ease = [0.22, 1, 0.36, 1];
const navLinks = ['Story', 'Expertise', 'Studios', 'Feedback'];

const menuVariants = {
  hidden: { opacity: 0, y: -10 },
  show:   { opacity: 1, y:   0, transition: { duration: 0.32, ease } },
  exit:   { opacity: 0, y: -10, transition: { duration: 0.2,  ease } },
};
const linkVariants = {
  hidden: { opacity: 0, y: 22 },
  show: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.07, duration: 0.4, ease } }),
  exit: (i) => ({ opacity: 0, y: -8, transition: { delay: (3 - i) * 0.04, duration: 0.18 } }),
};

export default function Nav() {
  const [scrolled, setScrolled]   = useState(false);
  const [menuOpen, setMenuOpen]   = useState(false);
  const shouldReduce              = useReducedMotion();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  const textColor   = '#0A0527';
  const underlineBg = '#0A0527';

  return (
    <>
      {/* ── Floating Nav Bar ────────────────────────────── */}
      <motion.nav
        initial={{ opacity: 0, y: shouldReduce ? 0 : -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.55, ease }}
        className="fixed top-4 left-0 right-0 z-40 px-4 sm:px-6"
      >
        <div
          className="max-w-6xl mx-auto flex items-center justify-between px-4 sm:px-6 py-2.5 rounded-2xl"
          style={{
            background: scrolled ? 'rgba(255,255,255,0.97)' : 'rgba(255,255,255,0.75)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            border: scrolled ? '1px solid rgba(24,24,27,0.10)' : '1px solid rgba(255,255,255,0.10)',
            boxShadow: scrolled ? '0 8px 32px -8px rgba(0,0,0,0.14)' : '0 4px 24px -4px rgba(0,0,0,0.3)',
            transition: 'background 350ms ease, border-color 350ms ease, box-shadow 350ms ease',
          }}
        >
          {/* ── LEFT: Logo + Name ──────────────────────── */}
          <a href="#hero" className="flex items-center gap-2.5 shrink-0">
            <SyenovixLogo height={32} />
          </a>

          {/* ── CENTER: Nav links (desktop) ────────────── */}
          <div className="hidden md:flex items-center gap-7 absolute left-1/2 -translate-x-1/2">
            {navLinks.map((link) => (
              <a
                key={link}
                href={`#${link.toLowerCase()}`}
                className="relative text-[12px] font-semibold tracking-widest uppercase pb-px"
                style={{ color: textColor, transition: 'color 350ms ease' }}
              >
                {link}
                <motion.span
                  className="absolute bottom-0 left-0 h-px w-full"
                  initial={{ scaleX: 0 }}
                  whileHover={{ scaleX: 1 }}
                  transition={{ duration: 0.2, ease }}
                  style={{ background: underlineBg, transformOrigin: 'left center', transition: 'background 350ms ease' }}
                />
              </a>
            ))}
          </div>

          {/* ── RIGHT: CTA + Hamburger ─────────────────── */}
          <div className="flex items-center gap-2.5 shrink-0">
            <motion.a
              href="#contact"
              whileTap={{ scale: 0.97 }}
              transition={{ duration: 0.1 }}
              className="hidden md:flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[11px] font-bold tracking-[0.1em] uppercase text-white"
              style={{ background: 'linear-gradient(135deg, #5E0ED7 0%, #2563EB 100%)', boxShadow: '0 4px 14px rgba(94,14,215,0.35)' }}
            >
              Work With Us
              <div className="w-4 h-4 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.2)' }}>
                <ArrowUpRight size={10} color="#fff" />
              </div>
            </motion.a>

            <motion.button
              whileTap={{ scale: 0.88 }}
              transition={{ duration: 0.1 }}
              onClick={() => setMenuOpen(true)}
              className="flex flex-col items-center justify-center gap-[4px] w-8 h-8 rounded-xl"
              style={{
                background: '#0A0527',
                transition: 'background 350ms ease',
              }}
              aria-label="Open menu"
            >
              <span className="w-3.5 h-px bg-white block" />
              <span className="w-3.5 h-px bg-white block" />
              <span className="w-3.5 h-px bg-white block" />
            </motion.button>
          </div>
        </div>
      </motion.nav>

      {/* ── Mobile / Full-screen Menu ───────────────────── */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            variants={menuVariants}
            initial="hidden"
            animate="show"
            exit="exit"
            className="fixed inset-0 z-50 flex flex-col px-6 sm:px-10 pt-6 pb-12"
            style={{ background: 'rgba(255,255,255,0.98)', backdropFilter: 'blur(32px)', WebkitBackdropFilter: 'blur(32px)' }}
          >
            {/* Top */}
            <div className="flex items-center justify-between mb-14">
              <a href="#hero" onClick={() => setMenuOpen(false)} className="flex items-center gap-2.5">
                <SyenovixLogo height={36} />
              </a>
              <motion.button
                whileTap={{ scale: 0.88 }}
                transition={{ duration: 0.1 }}
                onClick={() => setMenuOpen(false)}
                className="flex items-center justify-center w-9 h-9 rounded-xl"
                style={{ background: '#0A0527' }}
                aria-label="Close"
              >
                <div className="relative w-3.5 h-3.5">
                  <span className="absolute inset-0 m-auto block h-px w-full bg-white" style={{ transform: 'rotate(45deg)' }} />
                  <span className="absolute inset-0 m-auto block h-px w-full bg-white" style={{ transform: 'rotate(-45deg)' }} />
                </div>
              </motion.button>
            </div>

            {/* Links */}
            <div className="flex flex-col gap-5">
              {navLinks.map((link, i) => (
                <motion.a
                  key={link}
                  custom={i}
                  variants={linkVariants}
                  initial="hidden"
                  animate="show"
                  exit="exit"
                  href={`#${link.toLowerCase()}`}
                  onClick={() => setMenuOpen(false)}
                  whileTap={{ x: 4 }}
                  className="text-4xl sm:text-5xl font-bold tracking-tight group flex items-center gap-3"
                  style={{ color: '#0A0527' }}
                  style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                >
                  <span className="text-sm font-semibold tracking-widest" style={{ color: ACCENT }}>0{i + 1}</span>
                  {link}
                </motion.a>
              ))}
            </div>

            {/* Bottom CTA */}
            <motion.div
              custom={4}
              variants={linkVariants}
              initial="hidden"
              animate="show"
              exit="exit"
              className="mt-auto flex items-center justify-between"
            >
              <motion.a
                href="#contact"
                onClick={() => setMenuOpen(false)}
                whileTap={{ scale: 0.97 }}
                className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold tracking-wide text-white"
                style={{ background: ACCENT }}
              >
                Work With Us
                <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.18)' }}>
                  <ArrowUpRight size={13} color="#fff" />
                </div>
              </motion.a>
              <p className="text-xs text-right" style={{ color: 'rgba(0,0,0,0.3)' }}>
                Crafting the future<br />of AI software.
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
